import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
} from "@mui/icons-material";
import GlobalDashboardSidebar from "../admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "../admin/useGlobalSidebar";
import ReportsPortal from "./ReportsPortal";
import { USER_ROLE_ROUTE_CONFIG } from "../users/userRoleConfig";
import { logout } from "../../store/authSlice";
import "../admin/dashboard-redesign.css";
import "./Reports.css";

const REPORT_MENU_ITEMS = [
  "Overview",
  "Engine Report",
  "Device Logs Report",
  "Ignition Report",
  "ACC / Ignition Summary",
  "Daily Summary Report",
  "Trip History Report",
  "Trip Report",
  "Movement Report",
  "Distance Report",
  "Stoppage Report",
  "Overspeed Report",
  "Idle Report",
  "Fuel Report",
  "Geofence Report",
  "Temperature Report",
  "Travel Path Report",
  "Working Hours Report",
  "RFID Report",
  "View All Reports",
];

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks" },
  { key: "create", label: "Create Task" },
  { key: "inventory", label: "Inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const BILLING_MENU_ITEMS = [
  { key: "licenses", label: "Licenses" },
  { key: "all-licenses", label: "All Licenses" },
  { key: "purchase-license", label: "Purchase License" },
  { key: "license-summary", label: "License Summary" },
  { key: "transactions", label: "Transactions" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

const resolveSidebarReportType = (label) => {
  if (label === "Overview" || label === "View All Reports") return "Engine Report";
  return label;
};

export default function ReportsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.auth.user);

  const selectedReportMenu = searchParams.get("report") || "Overview";
  const selectedReportType = resolveSidebarReportType(selectedReportMenu);
  const selectedDeviceId = searchParams.get("deviceId") || "";
  const selectedBillingSection = searchParams.get("billingSection") || "licenses";
  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabel = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const setSelectedReport = useCallback((label) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("report", label);
      return next;
    });
  }, [setSearchParams]);

  const globalSidebarItemsLegacy = useMemo(
    () => [
      {
        key: "dashboard",
        label: "Dashboard",
        icon: <AssessmentOutlinedIcon fontSize="small" />,
        active: location.pathname === "/dashboard",
        onClick: () => navigate("/dashboard"),
      },
      {
        key: "vehicles",
        label: "Vehicles",
        icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />,
        active: location.pathname === "/admin-dashboard" && location.search.includes("tab=devices"),
        onClick: () => navigate("/admin-dashboard?tab=devices"),
      },
      {
        key: "live-tracking",
        label: "Live Tracking",
        icon: <PlaceOutlinedIcon fontSize="small" />,
        active: location.pathname === "/admin-dashboard" && location.search.includes("tab=map"),
        onClick: () => navigate("/admin-dashboard?tab=map"),
      },
      {
        key: "trips",
        label: "Trips",
        icon: <RouteOutlinedIcon fontSize="small" />,
        active: location.pathname === "/details",
        onClick: () => navigate("/logistic/trips"),
      },
      {
        key: "geofences",
        label: "Geofences",
        icon: <PlaceOutlinedIcon fontSize="small" />,
        active: location.pathname === "/geofences",
        onClick: () => navigate("/geofences"),
      },
      {
        key: "notifications",
        label: "Notifications",
        icon: <NotificationsActiveOutlinedIcon fontSize="small" />,
        active: location.pathname === "/notifications",
        onClick: () => navigate("/notifications"),
      },
      {
        key: "reports",
        label: "Reports",
        icon: <ReceiptLongOutlinedIcon fontSize="small" />,
        active: location.pathname === "/reports",
        defaultExpanded: true,
        children: REPORT_MENU_ITEMS.map((item) => ({
          key: item,
          label: item,
          active: selectedReportMenu === item,
          onClick: () => setSelectedReport(item),
        })),
      },
      {
        key: "tasks",
        label: "Tasks",
        icon: <TaskAltOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/tasks"),
        children: TASK_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === `/tasks/${item.key}`,
          onClick: () => navigate(`/tasks/${item.key}`),
        })),
      },
      {
        key: "messages",
        label: "Messages",
        icon: <MarkUnreadChatAltOutlinedIcon fontSize="small" />,
        active: location.pathname === "/message",
        children: MESSAGE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === "/message" && (new URLSearchParams(location.search).get("section") || "compose") === item.key,
          onClick: () => navigate(`/message?section=${item.key}`),
        })),
      },
      {
        key: "logistic",
        label: "Logistic",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/logistic"),
        children: [
          { key: "logistics-trips", label: "Trips", active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
          { key: "logistics-clients", label: "Clients", active: location.pathname === "/logistic/clients", onClick: () => navigate("/logistic/clients") },
          { key: "logistics-materials", label: "Materials", active: location.pathname === "/logistic/materials", onClick: () => navigate("/logistic/materials") },
          { key: "logistics-documents", label: "Documents", active: location.pathname === "/logistic/documents", onClick: () => navigate("/logistic/documents") },
        ],
      },
      {
        key: "organization",
        label: "Groups",
        icon: <Groups2OutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/groups"),
        onClick: () => navigate("/groups"),
      },
      {
        key: "reminders",
        label: "Reminders",
        icon: <EventNoteOutlinedIcon fontSize="small" />,
        active: location.pathname === "/reminders",
        onClick: () => navigate("/reminders"),
      },
      {
        key: "routes",
        label: "Routes",
        icon: <RouteOutlinedIcon fontSize="small" />,
        active: location.pathname === "/routes",
        onClick: () => navigate("/routes"),
      },
      {
        key: "expense",
        label: "Expense",
        icon: <PaidOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/expense"),
        children: EXPENSE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "sensors",
        label: "Sensors",
        icon: <SensorsRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/sensors"),
        children: SENSOR_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "users",
        label: "Users & Roles",
        icon: <PersonOutlineRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/users"),
        children: USER_ROLE_ROUTE_CONFIG.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "billing",
        label: "Billing & Licenses",
        icon: <AssessmentOutlinedIcon fontSize="small" />,
        active: location.pathname === "/licenses",
        children: BILLING_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === "/licenses" && selectedBillingSection === item.key,
          onClick: () => navigate(`/licenses?section=${item.key}`),
        })),
      },
      {
        key: "settings",
        label: "Settings",
        icon: <SettingsOutlinedIcon fontSize="small" />,
        active: location.pathname === "/profile",
        onClick: () => navigate("/profile"),
      },
    ],
    [location.pathname, location.search, navigate, selectedBillingSection, selectedReportMenu, setSelectedReport]
  );
  const globalSidebarItems = useGlobalSidebarItems();
  void globalSidebarItemsLegacy;

  return (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={globalSidebarItems}
        profileName={profileName}
        roleLabel={roleLabel}
        onSupport={() => navigate("/message")}
        onLogout={() => dispatch(logout())}
      />

      <main className="dashboard-content" style={{ gap: 18 }}>
        <ReportsPortal
          asPage
          initialReportType={selectedReportType}
          initialSelectedDeviceId={selectedDeviceId}
        />
      </main>
    </div>
  );
}
